import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserTutorials,
  selectUserTutorials,
  selectTutorialsLoading,
  selectTutorialsPagination,
  setTutorialCategory,
  setTutorialLevel,
  setTutorialSearchTerm,
  clearTutorialFilters,
  selectTutorialFilters
} from '../../../../../../../app/store/slices/userDashboardSlice';
import { useCanvas } from "../../../../../context/CanvasContext";
import { CircularProgress, Chip } from '@mui/material';
import { FiPlay, FiSearch } from 'react-icons/fi';
import TutorialVideoPlayer from './TutorialVideoPlayer';

const LEVEL_COLORS = {
  beginner: 'bg-green-100 text-green-800',
  intermediate: 'bg-yellow-100 text-yellow-800',
  advanced: 'bg-red-100 text-red-800'
};

const TutorialsList = () => {
  const dispatch = useDispatch();
  const tutorials = useSelector(selectUserTutorials);
  const loading = useSelector(selectTutorialsLoading);
  const pagination = useSelector(selectTutorialsPagination);
  const filters = useSelector(selectTutorialFilters);

  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const { selectedCanvas } = useCanvas();

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchUserTutorials({ 
        canvasId: selectedCanvas.id,
        category: filters.category,
        level: filters.level,
        searchTerm: filters.searchTerm
      }));
    }
  }, [dispatch, selectedCanvas?.id]);

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'category':
        dispatch(setTutorialCategory(value));
        break;
      case 'level':
        dispatch(setTutorialLevel(value));
        break;
      case 'search':
        dispatch(setTutorialSearchTerm(value));
        break;
      default:
        break;
    }
    
    if (selectedCanvas?.id) {
      dispatch(fetchUserTutorials({ 
        canvasId: selectedCanvas.id,
        category: filterType === 'category' ? value : filters.category,
        level: filterType === 'level' ? value : filters.level,
        searchTerm: filterType === 'search' ? value : filters.searchTerm
      }));
    }
  };

  const handleClearFilters = () => {
    dispatch(clearTutorialFilters());
    setLocalSearch('');
    if (selectedCanvas?.id) {
      dispatch(fetchUserTutorials({ canvasId: selectedCanvas.id }));
    }
  };

  const handlePlayVideo = (tutorial) => {
    setSelectedTutorial(tutorial);
    setPlayerOpen(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange('search', localSearch);
  };

  // Get unique categories from tutorials
  const categories = [...new Set(tutorials.filter(t => t.category).map(t => t.category))];

  const hasActiveFilters = filters.category || filters.level || filters.searchTerm;

  if (!selectedCanvas?.id) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center text-gray-500">
          <p className="text-lg mb-4">Please select a canvas to view tutorials</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-center mb-2 text-btn-primary">
          Video Tutorials
        </h1>
        <p className="text-center text-gray-600 max-w-2xl mx-auto">
          Watch step-by-step guides to master Cash Canvas and take control of your finances
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="col-span-1 md:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tutorials..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </form>

          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Level Filter */}
          <select
            value={filters.level}
            onChange={(e) => handleFilterChange('level', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600">Active filters:</span>
            {filters.category && (
              <Chip
                label={`Category: ${filters.category}`}
                onDelete={() => handleFilterChange('category', '')}
                size="small"
                color="primary"
              />
            )}
            {filters.level && (
              <Chip
                label={`Level: ${filters.level}`}
                onDelete={() => handleFilterChange('level', '')}
                size="small"
                color="primary"
              />
            )}
            {filters.searchTerm && (
              <Chip
                label={`Search: ${filters.searchTerm}`}
                onDelete={() => {
                  setLocalSearch('');
                  handleFilterChange('search', '');
                }}
                size="small"
                color="primary"
              />
            )}
            <button
              onClick={handleClearFilters}
              className="text-sm text-btn-primary hover:text-btn-primary-hover underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Tutorials Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <CircularProgress />
        </div>
      ) : tutorials.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-500 text-lg mb-4">No tutorials found</p>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-btn-primary text-btn-primary-text rounded-lg hover:bg-btn-primary-hover"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tutorials.map((tutorial) => (
              <div
                key={tutorial._id || tutorial.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
                onClick={() => handlePlayVideo(tutorial)}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gray-900">
                  {tutorial.thumbnailUrl ? (
                    <img
                      src={tutorial.thumbnailUrl}
                      alt={tutorial.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-btn-primary to-btn-primary-hover">
                      <FiPlay size={48} className="text-white opacity-75" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity transform scale-90 group-hover:scale-100">
                      <FiPlay size={24} className="text-btn-primary ml-1" />
                    </div>
                  </div>
                  {tutorial.duration && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {tutorial.duration}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-btn-primary transition-colors">
                    {tutorial.title}
                  </h3>
                  {tutorial.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{tutorial.description}</p>
                  )}

                  {/* Meta */}
                  <div className="flex flex-wrap gap-2 items-center">
                    {tutorial.level && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${LEVEL_COLORS[tutorial.level]}`}>
                        {tutorial.level.charAt(0).toUpperCase() + tutorial.level.slice(1)}
                      </span>
                    )}
                    {tutorial.category && (
                      <span className="text-xs text-gray-500">{tutorial.category}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination info */}
          {pagination && pagination.total > 0 && (
            <div className="mt-6 text-center text-sm text-gray-600">
              Showing {tutorials.length} of {pagination.total} tutorials
            </div>
          )}
        </>
      )}

      {/* Video Player Modal */}
      <TutorialVideoPlayer
        open={playerOpen}
        onClose={() => setPlayerOpen(false)}
        tutorial={selectedTutorial}
      />
    </div>
  );
};

export default TutorialsList;